import { generateFontStyleCSS } from '@thoughtspot/formatter-service';
import { getId, getTooltipInformationModel, isEffectivelyNumeric, supportsAggregationChange, supportsDateBucketizationChange, } from '@thoughtspot/visualization-column-util';
import _ from 'lodash';
import { getAxisAdvancedPropertiesById, getAxisName, getAxisProperties, getChartType, getSortOrderByColId, getXAxes, getYAxes, isAscendingSort, isSortKey, } from '../core/chart-model-utils';
import { configOptions } from '../core/chart-type-specification-service';
import { getName } from '../core/column-name-util';
import { getEnvFlags } from '../env';
import { ColumnSortCategory, } from '../types';
const AXIS_CONTAINER_CLASS = '.bk-axis-label-container';
const AXIS_ORIENTATION_CLASS = {
    VERTICAL: 'bk-vertical-axis',
    HORIZONTAL: 'bk-horizontal-axis',
};
const chartsAxisEventsMap = new Map();
export const unbindAxisEvents = (chartId) => {
    const eventHandlerMap = chartsAxisEventsMap.get(chartId);
    if (eventHandlerMap) {
        eventHandlerMap.forEach((handler, el) => {
            if (el) {
                el.removeEventListener('click', handler);
            }
        });
        eventHandlerMap.clear();
    }
};
export function bindAxisEvents(chartEl, chartModel, onAxisClick, onColumnSort) {
    const axisEventsMap = new Map();
    unbindAxisEvents(chartModel.id);
    chartEl.querySelectorAll('.bk-axis-label-control').forEach(axisLabelEl => {
        const axisId = axisLabelEl.closest(AXIS_CONTAINER_CLASS).id;
        const axisProps = getAxisProperties(chartModel);
        if (_.isFunction(onAxisClick)) {
            const eventHandler = (event) => onAxisClick(event, axisId);
            axisLabelEl.addEventListener('click', eventHandler);
            axisEventsMap.set(axisLabelEl, eventHandler);
        }
    });
    chartEl.querySelectorAll('.bk-sort-indicator').forEach(columnSortEl => {
        const columnId = columnSortEl.id;
        const sortInfo = getSortOrderByColId(chartModel, columnId);
        const isAscending = sortInfo === null || sortInfo === void 0 ? void 0 : sortInfo.sortAscending;
        if (_.isFunction(onColumnSort)) {
            const eventHandler = (event) => onColumnSort(event, columnId, !isAscending, sortInfo);
            columnSortEl.addEventListener('click', eventHandler);
            axisEventsMap.set(columnSortEl, eventHandler);
        }
    });
    chartsAxisEventsMap.set(chartModel.id, axisEventsMap);
}
export function getDirectionClass(chartModel, col, isVertical, isOpp) {
    const isAxisPosRight = isVertical && isOpp;
    if (isSortKey(chartModel, col)) {
        if (isAscendingSort(chartModel, col)) {
            return isAxisPosRight ? 'down' : 'up';
        }
        return isAxisPosRight ? 'up' : 'down';
    }
    return null;
}
function hasAvailableInteraction(vizColumn, isEditable) {
    return ((isEffectivelyNumeric(vizColumn) && isEditable) ||
        isEditable ||
        (supportsAggregationChange(vizColumn) && isEditable) ||
        (supportsDateBucketizationChange(vizColumn) && isEditable));
}
function getAxisHTML(columnName, id, showIcon = true, sortedColumnList, isVertical, tooltip, chartModel, columnNameSuffix) {
    var _a, _b, _c, _d, _e;
    const axisClass = isVertical
        ? AXIS_ORIENTATION_CLASS.VERTICAL
        : AXIS_ORIENTATION_CLASS.HORIZONTAL;
    const renderColumnNameSuffix = columnNameSuffix
        ? `<i class="axis-label-title-suffix">${columnNameSuffix}</i>`
        : '';
    let actionIcon = '';
    let iconPlaceholder = '';
    if (showIcon) {
        if (sortedColumnList.length > 0) {
            iconPlaceholder = 'bk-icon-arrow-down-placeholder';
            actionIcon = '<div class="bk-icon-arrow-down" style="display: none;"></div>';
        }
        else {
            actionIcon = '<div class="bk-icon-arrow-down" style="visibility: hidden;"></div>';
        }
    }
    const sortIcons = sortedColumnList.reduce((iconHTML, item) => {
        var _a;
        if (getEnvFlags().isSortV2Enabled) {
            const sortInfo = (_a = chartModel.sortInfo) === null || _a === void 0 ? void 0 : _a.find(col => col.columnId === item.column.id);
            if ((sortInfo === null || sortInfo === void 0 ? void 0 : sortInfo.category) === ColumnSortCategory.None) {
                return '';
            }
        }
        const arrowPath = 'm9.998 4.6 4.415 4.395-1.411 1.418-2.003-1.994.001 6.581h-2l-.001-6.583-2.004 1.996-1.411-1.418z';
        return `${iconHTML}<div class="bk-axis-sort-icon" data-tooltip-content="${getName(item.column)}">
            <svg id="${getId(item.column)}" class="bk-sort-indicator ${item.direction}" width="20" height="20" viewBox="0 0 20 20">
                <path d="${arrowPath}"/>
            </svg></div>`;
    }, '');
    const cursorClass = showIcon ? 'bk-cursor' : 'bk-no-cursor';
    const tooltipAlign = isVertical ? 'left' : 'center';
    let customTextStyle = '';
    if (getEnvFlags().isHighchartCustomisationEnabled) {
        const advancedAxisStyleSettings = (_b = (_a = getAxisAdvancedPropertiesById(chartModel.vizProp, id)) === null || _a === void 0 ? void 0 : _a.axisSettings) === null || _b === void 0 ? void 0 : _b.axisName;
        const chartConfigOptions = configOptions(getChartType(chartModel), chartModel);
        const isXYAxisTransposed = chartConfigOptions.allowedConfigurations.transposeXY;
        const baseAxes = isXYAxisTransposed
            ? getYAxes(chartModel)
            : getXAxes(chartModel);
        const fallbackStyleSettings = (_e = (_d = getAxisAdvancedPropertiesById(chartModel.vizProp, (_c = baseAxes[0]) === null || _c === void 0 ? void 0 : _c.id)) === null || _d === void 0 ? void 0 : _d.axisSettings) === null || _e === void 0 ? void 0 : _e.axisName;
        if (advancedAxisStyleSettings) {
            const styleSettings = advancedAxisStyleSettings &&
                Object.keys(advancedAxisStyleSettings).length > 1
                ? advancedAxisStyleSettings
                : fallbackStyleSettings;
            customTextStyle = generateFontStyleCSS(advancedAxisStyleSettings);
        }
    }
    return `<div id="${id}" class="bk-axis-label-container">
                <div class="bk-axis-label ${cursorClass} ${axisClass} ${iconPlaceholder}">
                    <div class="bk-axis-label-control" data-tooltip-content="${tooltip}" data-tooltip-align="${tooltipAlign}">
                        <div class="axis-label-title" style="${customTextStyle}">
                            ${_.escape(columnName)} ${renderColumnNameSuffix}
                        </div>
                        ${actionIcon}
                    </div>
                    ${sortIcons}
                </div>
            </div>`;
}
export function getAxisTemplate(axisId, chartModel, columns, isVertical, disableTransformations, isOpposite = false, getTranslatedText, enableMobileOverride, columnNameSuffix) {
    const hasPermsToEdit = true;
    const canEdit = !disableTransformations && hasPermsToEdit;
    const combinedAxisName = getAxisName(chartModel, axisId, getTranslatedText);
    const sortedColumnList = columns.reduce((sortedColumns, column) => {
        if (isSortKey(chartModel, column))
            sortedColumns.push({
                column,
                direction: getDirectionClass(chartModel, column, isVertical, isOpposite),
            });
        return sortedColumns;
    }, []);
    const showIconArrow = !enableMobileOverride &&
        columns.some(col => hasAvailableInteraction(col, canEdit));
    const tooltip = columns
        .map(col => getTooltipInformationModel(col, getTranslatedText, columnNameSuffix).getText())
        .join('\n\n');
    return getAxisHTML(combinedAxisName, axisId, showIconArrow, sortedColumnList, isVertical, tooltip, chartModel, columnNameSuffix);
}
function isSortedByColumn(vizColumn, isEditable) {
    return vizColumn.isSortKey() && isEditable;
}
//# sourceMappingURL=highchart-axis-renderer.js.map